// resources/js/admin/pages/module-pages/media/MediaPage.tsx

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, X, Trash2, Upload, Image, Video, File } from "lucide-react";
import PageListLayout from "../../../layouts/PageListLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import MediaList from "../../../components/shared/media/MediaList";
import MediaUploader from "../../../components/shared/media/MediaUploader";
import { Media } from "../../../types/features/Media";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { useProduct } from "../../../hooks/core/useProduct";
import useMedia from "../../../hooks/features/media/useMedia";

export default function MediaPage() {
    const navigate = useNavigate();
    const { selectedProduct } = useProduct();
    const {
        media,
        loading,
        deleteMedia,
        deleteManyMedia,
        uploadMedia,
        filterType,
        filterByType,
        searchMedia,
    } = useMedia(selectedProduct || "izi-call");

    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [showUploader, setShowUploader] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
    const [mediaToDelete, setMediaToDelete] = useState<Media | null>(null);
    const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedType, setSelectedType] = useState<string>("");

    // Filtrer les médias
    const filteredMedia = useMemo(() => {
        let result = media;

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            result = result.filter(
                (m) =>
                    m.filename.toLowerCase().includes(searchLower) ||
                    (m.alt_text && m.alt_text.toLowerCase().includes(searchLower))
            );
        }

        if (selectedType) {
            result = result.filter((m) => {
                if (selectedType === "image") return m.mime_type.startsWith("image/");
                if (selectedType === "video") return m.mime_type.startsWith("video/");
                if (selectedType === "document") return m.mime_type.startsWith("application/");
                return true;
            });
        }

        return result;
    }, [media, searchTerm, selectedType]);

    // Stats
    const stats = {
        total: media.length,
        images: media.filter((m) => m.mime_type.startsWith("image/")).length,
        videos: media.filter((m) => m.mime_type.startsWith("video/")).length,
        documents: media.filter((m) => m.mime_type.startsWith("application/")).length,
    };

    const handleUpload = async (file: File, altText?: string) => {
        setIsUploading(true);
        const success = await uploadMedia({
            site_slug: selectedProduct || "izi-call",
            file,
            alt_text: altText,
            is_public: true,
        });
        setIsUploading(false);
        if (success) {
            alertSuccess("Fichier téléchargé avec succès");
            setShowUploader(false);
        } else {
            alertError("Erreur lors du téléchargement");
        }
    };

    const handleDelete = async () => {
        if (!mediaToDelete) return;
        setIsDeleting(true);
        const success = await deleteMedia(mediaToDelete.id);
        setIsDeleting(false);
        if (success) {
            setMediaToDelete(null);
            alertSuccess("Média supprimé avec succès");
        } else {
            alertError("Erreur lors de la suppression");
        }
    };

    const handleDeleteMany = async () => {
        if (!itemsToDelete || itemsToDelete.length === 0) return;
        const success = await deleteManyMedia(itemsToDelete);
        if (success) {
            setItemsToDelete(null);
            alertSuccess(`${itemsToDelete.length} médias supprimés`);
        } else {
            alertError("Erreur lors de la suppression multiple");
        }
    };

    const clearFilters = () => {
        setSearchTerm("");
        setSelectedType("");
    };

    const hasActiveFilters = searchTerm || selectedType;

    const getActionsMenu = (media: Media): Menu[] => {
        return [
            {
                label: "Voir",
                icon: Search,
                onClick: () => navigate(`/admin/media/${media.id}`),
            },
            {
                label: "Supprimer",
                icon: Trash2,
                onClick: () => setMediaToDelete(media),
            },
        ];
    };

    return (
        <PageListLayout title="Gestion des médias">
            <div className="px-6 py-4 bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Médias</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Gérez les images, vidéos et documents du site
                            </p>
                        </div>
                        <button
                            onClick={() => setShowUploader(!showUploader)}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                        >
                            <Upload size={18} />
                            {showUploader ? "Fermer" : "Télécharger"}
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 mt-6">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                            <p className="text-xs text-blue-700/70">Total</p>
                            <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                            <p className="text-xs text-green-700/70">Images</p>
                            <p className="text-2xl font-bold text-green-700">{stats.images}</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                            <p className="text-xs text-purple-700/70">Vidéos</p>
                            <p className="text-2xl font-bold text-purple-700">{stats.videos}</p>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
                            <p className="text-xs text-orange-700/70">Documents</p>
                            <p className="text-2xl font-bold text-orange-700">{stats.documents}</p>
                        </div>
                    </div>

                    {/* Uploader */}
                    {showUploader && (
                        <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <MediaUploader
                                siteSlug={selectedProduct || "izi-call"}
                                onUpload={handleUpload}
                                isLoading={isUploading}
                            />
                        </div>
                    )}

                    {/* Recherche */}
                    <div className="relative mt-6">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher un fichier..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                searchMedia(e.target.value);
                            }}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                        />
                    </div>

                    {/* Filtres */}
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                                showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            <Filter size={18} /> Filtrer
                            {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
                        </button>

                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-700"
                            >
                                <X size={14} /> Effacer tout
                            </button>
                        )}
                    </div>

                    {showFilters && (
                        <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => setSelectedType("")}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            selectedType === ""
                                                ? "bg-primary text-white"
                                                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        Tous
                                    </button>
                                    <button
                                        onClick={() => setSelectedType("image")}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                            selectedType === "image"
                                                ? "bg-primary text-white"
                                                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        <Image size={16} /> Images
                                    </button>
                                    <button
                                        onClick={() => setSelectedType("video")}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                            selectedType === "video"
                                                ? "bg-primary text-white"
                                                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        <Video size={16} /> Vidéos
                                    </button>
                                    <button
                                        onClick={() => setSelectedType("document")}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                            selectedType === "document"
                                                ? "bg-primary text-white"
                                                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        <File size={16} /> Documents
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-6 py-8 max-w-7xl mx-auto">
                <MediaList
                    media={filteredMedia}
                    onAction={(media) => setSelectedMedia(media)}
                    onSelectMedia={(selected) => setItemsToDelete(selected.map((m) => m.id))}
                    selectable={true}
                    loading={loading}
                    selectActions={[
                        {
                            label: "Supprimer",
                            onClick: (selected) => setItemsToDelete(selected.map((m) => m.id)),
                            className: "bg-red-600 text-white",
                        },
                    ]}
                />
            </div>

            {/* Menu modal */}
            {selectedMedia && (
                <MenuModal
                    title={selectedMedia.filename}
                    isOpen={!!selectedMedia}
                    onClose={() => setSelectedMedia(null)}
                    icon={null}
                    menu={getActionsMenu(selectedMedia)}
                />
            )}

            {/* Modals suppression */}
            <DeleteConfirmationModal
                isOpen={!!mediaToDelete}
                onClose={() => setMediaToDelete(null)}
                onConfirm={handleDelete}
                title="Supprimer le média"
                message={`Supprimer le fichier "${mediaToDelete?.filename}" ? Cette action est irréversible.`}
                confirmText={isDeleting ? "Suppression..." : "Supprimer"}
            />

            <DeleteConfirmationModal
                isOpen={!!itemsToDelete && itemsToDelete.length > 0}
                onClose={() => setItemsToDelete(null)}
                onConfirm={handleDeleteMany}
                title="Suppression multiple"
                message={`Supprimer ${itemsToDelete?.length} média(s) ?`}
            />
        </PageListLayout>
    );
}